/**
 * @file GlowingEffect 组件
 * @author alfielv
 */
import React, { memo, useCallback, useEffect, useRef } from 'react';
import classNames from 'classnames';

/**
 * 合并类名
 * @param inputs 类名参数
 * @returns 合并后的类名字符串
 */
export function cn(...inputs: (string | undefined | null | boolean)[]): string {
  return classNames(inputs);
}

interface GlowingEffectProps {
  blur?: number;
  /**
   * 中心不活跃区域比例
   */
  inactiveZone?: number;
  /**
   * 鼠标激活范围 (px)
   */
  proximity?: number;
  /**
   * 渐变扇形角度 (度)
   */
  spread?: number;
  variant?: 'default' | 'white';
  /**
   * 是否显示静态边框
   */
  glow?: boolean;
  className?: string;
  disabled?: boolean;
  /**
   * 动画持续时间 (秒)
   */
  movementDuration?: number;
  /**
   * 边框宽度 (px)
   */
  borderWidth?: number;
}
/**
 * 缓动函数 - cubic-bezier(0.16, 1, 0.3, 1)
 * @param t 进度值 (0-1)
 * @returns 缓动后的值
 */
const easeOutCubic = (t: number): number => 1 - (1 - t) ** 3;

/**
 * 动画函数 - 兼容 React 16
 * @param start 起始值
 * @param end 结束值
 * @param duration 持续时间（秒）
 * @param onUpdate 更新回调
 * @returns 取消动画的函数
 */
const animate = (
  start: number,
  end: number,
  options: { duration: number; ease?: (t: number) => number; onUpdate: (value: number) => void },
): (() => void) => {
  const { duration, ease = easeOutCubic, onUpdate } = options;
  const startTime = performance.now();
  const difference = end - start;
  let rafId: number | null = null;
  let cancelled = false;

  const step = (currentTime: number): void => {
    if (cancelled) return;

    const elapsed = (currentTime - startTime) / 1000;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = ease(progress);
    const currentValue = start + difference * easedProgress;

    onUpdate(currentValue);

    if (progress < 1 && !cancelled) {
      rafId = requestAnimationFrame(step);
    }
  };

  rafId = requestAnimationFrame(step);

  return () => {
    cancelled = true;
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
    }
  };
};

const GlowingEffect = memo(({
  blur = 0,
  inactiveZone = 0.7,
  proximity = 0,
  spread = 20,
  variant = 'white',
  glow = false,
  className,
  movementDuration = 2,
  borderWidth = 1,
  disabled = true,
}: GlowingEffectProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const lastPosition = useRef({ x: 0, y: 0 });
  const animationFrameRef = useRef<number>(0);
  const cancelAnimationRef = useRef<(() => void) | null>(null);

  const handleMove = useCallback(
    (e?: MouseEvent | { x: number; y: number }) => {
      if (!containerRef.current) return;

      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      animationFrameRef.current = requestAnimationFrame(() => {
        const element = containerRef.current;
        if (!element) return;

        const { left, top, width, height } = element.getBoundingClientRect();
        const mouseX = e?.x ?? lastPosition.current.x;
        const mouseY = e?.y ?? lastPosition.current.y;

        if (e) {
          lastPosition.current = { x: mouseX, y: mouseY };
        }

        const center = [left + width * 0.5, top + height * 0.5];
        const distanceFromCenter = Math.hypot(
          mouseX - center[0],
          mouseY - center[1],
        );
        const inactiveRadius = 0.5 * Math.min(width, height) * inactiveZone;

        if (distanceFromCenter < inactiveRadius) {
          element.style.setProperty('--active', '0');
          return;
        }

        const isActive = mouseX > left - proximity
            && mouseX < left + width + proximity
            && mouseY > top - proximity
            && mouseY < top + height + proximity;

        element.style.setProperty('--active', isActive ? '1' : '0');

        if (!isActive) return;

        const currentAngle =            parseFloat(element.style.getPropertyValue('--start')) || 0;
        const targetAngle =            (180 * Math.atan2(mouseY - center[1], mouseX - center[0]))
              / Math.PI
            + 90;

        const angleDiff = ((targetAngle - currentAngle + 180) % 360) - 180;
        const newAngle = currentAngle + angleDiff;

        // 取消之前的动画
        if (cancelAnimationRef.current) {
          cancelAnimationRef.current();
          cancelAnimationRef.current = null;
        }

        // 启动新动画
        cancelAnimationRef.current = animate(currentAngle, newAngle, {
          duration: movementDuration,
          onUpdate: (value) => {
            element.style.setProperty('--start', String(value));
          },
        });
      });
    },
    [inactiveZone, proximity, movementDuration],
  );

  useEffect(() => {
    if (disabled) return;

    const handleScroll = () => handleMove();
    const handlePointerMove = (e: PointerEvent) => handleMove(e);

    window.addEventListener('scroll', handleScroll, { passive: true });
    document.body.addEventListener('pointermove', handlePointerMove, {
      passive: true,
    });

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (cancelAnimationRef.current) {
        cancelAnimationRef.current();
        cancelAnimationRef.current = null;
      }
      window.removeEventListener('scroll', handleScroll);
      document.body.removeEventListener('pointermove', handlePointerMove);
    };
  }, [handleMove, disabled]);

  const randomId = Math.random()
    .toString(36)
    .substring(2, 11);
  const glowIdRef = useRef<string>(`glow-${randomId}`);

  const containerStyle: Record<string, string> = {
    // NOTE:定制颜色
    '--black': '#D4C5FF',
    // 原生配置
    '--blur': `${blur}px`,
    '--spread': String(spread),
    '--start': '0',
    '--active': '0',
    '--glowingeffect-border-width': `${borderWidth}px`,
    '--repeating-conic-gradient-times': '5',
    '--mask-border': 'linear-gradient(#000, #000)',
    '--mask-content': 'linear-gradient(#000, #000)',
    '--mask-conic': 'conic-gradient(from calc((var(--start) - var(--spread)) * 1deg), transparent 0deg, white calc(var(--spread) * 1deg), transparent calc(var(--spread) * 2deg), transparent 360deg)',
    '--gradient':
      variant === 'white'
        ? `repeating-conic-gradient(
          from 236.84deg at 50% 50%,
          var(--black),
          var(--black) calc(25% / var(--repeating-conic-gradient-times))
        )`
        : `radial-gradient(circle, #dd7bbb 10%, #dd7bbb00 20%),
        radial-gradient(circle at 40% 40%, #d79f1e 5%, #d79f1e00 15%),
        radial-gradient(circle at 60% 60%, #5a922c 10%, #5a922c00 20%),
        radial-gradient(circle at 40% 60%, #4c7894 10%, #4c789400 20%),
        repeating-conic-gradient(
          from 236.84deg at 50% 50%,
          #dd7bbb 0%,
          #d79f1e calc(25% / var(--repeating-conic-gradient-times)),
          #5a922c calc(50% / var(--repeating-conic-gradient-times)),
          #4c7894 calc(75% / var(--repeating-conic-gradient-times)),
          #dd7bbb calc(100% / var(--repeating-conic-gradient-times))
        )`,
  };

  return (
      <>
        <style>
          {`
            .${glowIdRef.current} {
              position: relative;
              width: 100%;
              height: 100%;
            }
            .${glowIdRef.current}::after {
              content: "";
              position: absolute;
              inset: 0;
              border-radius: inherit;
              padding: var(--glowingeffect-border-width);
              background: var(--gradient);
              opacity: var(--active);
              transition: opacity 300ms;
              pointer-events: none;
              -webkit-mask-image: var(--mask-conic), linear-gradient(#fff 0 0), linear-gradient(#fff 0 0);
              mask-image: var(--mask-conic), linear-gradient(#fff 0 0), linear-gradient(#fff 0 0);
              -webkit-mask-clip: padding-box, padding-box, content-box;
              mask-clip: padding-box, padding-box, content-box;
              -webkit-mask-composite: source-in, xor;
              mask-composite: intersect, exclude;
            }
          `}
        </style>
        <div
          className={cn(
            'tw-pointer-events-none tw-absolute tw--inset-px tw-hidden tw-rounded-[inherit] tw-border tw-border-slate-200 tw-opacity-0 tw-transition-opacity',
            glow && 'tw-opacity-100',
            variant === 'white' && 'tw-border-white',
            disabled && 'tw-!block',
          )}
        />
        <div
          ref={containerRef}
          style={containerStyle}
          className={cn(
            'tw-pointer-events-none tw-absolute tw-inset-0 tw-rounded-[inherit] tw-opacity-100 tw-transition-opacity',
            glow && 'tw-opacity-100',
            blur > 0 && 'tw-blur-[var(--blur)]',
            className,
            disabled && 'tw-!hidden',
          )}
        >
          <div
            className={cn(
              'tw-glow',
              'tw-rounded-[inherit]',
              glowIdRef.current,
            )}
          />
        </div>
      </>
  );
});

GlowingEffect.displayName = 'GlowingEffect';

export { GlowingEffect };
